import React, { useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownList,
  FileUploader,
  Input,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import ProcurementStatusTag from 'components/base/procurement-status-tag';
import useProcurementContext from 'contexts/procurement.context/context';
import CurrencyInput from 'components/base/currency-input';
import { useNavigate } from 'react-router-dom';
import { UpdateProcurementType } from 'models/procurement.model';
import { apiService } from 'services/base.service';
import { Calendar } from 'react-date-range';
import { toast } from 'react-toastify';
import { ArrowDownIcon } from 'cokitchen-icons';
import moment from 'moment';
import './styles.scss';
import { FILE_UPLOADER_TOKEN } from 'utils/constants';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';

const UpdateKitchenRequestDetails = () => {
  const { procurementToUpdate, setProcurementToUpdate } = useProcurementContext();
  const [isUpdatingProcurement, setIsUpdatingProcurement] = useState(false);
  const [showDateModal, setShowDateModal] = useState({ idx: -1, show: false });

  const router = useNavigate();

  useEffect(() => {
    procurementToUpdate.items.map((item, index) => {
      setValue(index, 'new_quantity_received', item.quantity_received);
    });
  }, []);

  const updateProcurementDetails = async () => {
    const data = {
      items: procurementToUpdate.items
        .filter((item) => !item.completed)
        .map((procurement_detail) => {
          return {
            id: procurement_detail.id,
            quantity_received:
              Number(procurement_detail.new_quantity_received) -
                Number(procurement_detail.quantity_received) || 0,
            quantity_returned: Number(procurement_detail.quantity_returned) || 0,
            price_per_unit:
              Number(
                (
                  Number(procurement_detail.actual_price) /
                  Number(procurement_detail.new_quantity_received)
                ).toFixed(2)
              ) || 0,
            completed: procurement_detail.is_complete ? true : false,
            note: '.',
            expiration_date: moment(procurement_detail.expiryDate).toISOString()
          };
        }),
      note: '.'
    };
    try {
      setIsUpdatingProcurement(true);
      const response = await apiService.post(
        `procurement-transaction-confirmation/procurements/items/${procurementToUpdate.id}/confirm-received`,
        { data }
      );
      if (response) {
        toast(<ToastMessage message='Procurement details updated successfully' type='success' />);
        router(-1);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating procurement request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingProcurement(false);
    }
  };

  const setValue = (index: number, field: keyof UpdateProcurementType, value: string) => {
    setProcurementToUpdate((prev) => ({
      ...prev,
      items: prev.items.map((data, i) => (i === index ? { ...data, [field]: value } : data))
    }));
  };

  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setProcurementToUpdate((prev) => ({
      ...prev,
      items: prev.items.map((data, i) => (i === index ? { ...data, is_complete: checked } : data))
    }));
  };

  const isFormFilled = () => {
    return procurementToUpdate.items.every((item) => {
      return !item.is_complete || (item.quantity_received && item.actual_price);
    });
  };

  return (
    <RequestDetailsWrapper
      codeId={procurementToUpdate.code}
      statusTag={<ProcurementStatusTag type={procurementToUpdate.status} />}
      header='Update request'
      subHeader='State the quantity received in good condition where necessary'>
      {procurementToUpdate.items.map((item, index) =>
        item.completed ? (
          ''
        ) : (
          <div key={index} className='pt-6'>
            <div className='flex justify-end items-center px-[28px]'>
              <Input
                type='checkbox'
                className='mr-2'
                checked={item.is_complete || false}
                onChange={(e) => handleChange(index, e)}
              />
              <p className='text-[14px] text-[#6F7376]'>Mark as completed</p>
            </div>
            <div className='w-full flex pb-[24px] px-[28px] border-b border-b-[#F7F7F8]'>
              <div className='w-full grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6'>
                <Input
                  design='bordered'
                  label='Item Name'
                  name='name'
                  placeholder='Item Name'
                  value={item.ingredient.name}
                  disabled
                />
                <Input
                  design='bordered'
                  label='Vendor'
                  name='vendor'
                  placeholder='Vendor'
                  value={item.vendor.name}
                  disabled
                />
                <CurrencyInput
                  label='Anticipated price'
                  placeholder='Anticipated price'
                  name='price'
                  value={item.price}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={() => {
                    return;
                  }}
                  disabled
                />
                <CurrencyInput
                  label='Actual price'
                  placeholder=' 00'
                  name='actual_price'
                  hasUnit
                  unitValue='NGN'
                  explicitRender
                  disableUnit
                  value={item.actual_price}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(value) => {
                    setValue(index, 'actual_price', value);
                  }}
                />
                <CurrencyInput
                  label='Price per unit'
                  placeholder=' 00'
                  name='price_per_unit'
                  hasUnit
                  unitValue='NGN'
                  explicitRender
                  disableUnit
                  disabled
                  value={
                    Number(item.new_quantity_received)
                      ? (Number(item.actual_price) / Number(item.new_quantity_received)).toFixed(2)
                      : ''
                  }
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(value) => {
                    setValue(index, 'actual_price', value);
                  }}
                />

                <CurrencyInput
                  label='Quantity requested'
                  placeholder='00'
                  name='quantity_requested'
                  value={item.quantity}
                  hasUnit
                  unitValue={item.ingredient.unit_type}
                  disableUnit
                  hideCurrency
                  explicitRender
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={() => {
                    return;
                  }}
                  disabled
                />
                <CurrencyInput
                  label='Quantity received'
                  placeholder='00'
                  name='quantity_received'
                  hasUnit
                  unitValue={item.ingredient.unit_type}
                  disableUnit
                  hideCurrency
                  explicitRender
                  value={item.new_quantity_received}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(value) => {
                    Number(value) <= Number(item.quantity) &&
                      setValue(index, 'new_quantity_received', value);
                  }}
                />
                <CurrencyInput
                  label='Quantity returned'
                  placeholder='Quantity returned'
                  name='quantity_returned'
                  hasUnit
                  unitValue={item.ingredient.unit_type}
                  disableUnit
                  hideCurrency
                  explicitRender
                  value={item.quantity_returned}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(value) => {
                    Number(value) <= Number(item.quantity) &&
                      setValue(index, 'quantity_returned', value);
                  }}
                />

                <div className='flex flex-col gap-1'>
                  <span>Expiry Date</span>
                  <Dropdown
                    visible={showDateModal.idx === index && showDateModal.show}
                    onClickOutside={() => setShowDateModal({ idx: -1, show: false })}
                    content={
                      <div
                        className='w-full flex justify-between bg-gray-1/20 py-3.5 px-2 border-b-2 border-b-gray-3/60'
                        onClick={() => setShowDateModal({ show: !showDateModal.show, idx: index })}>
                        <p className={`text-sm ${item.expiryDate ? '' : 'text-gray-5'}`}>
                          {item.expiryDate
                            ? moment(item.expiryDate).format(' MM/YY')
                            : 'Select a date (MM/YY)'}
                        </p>
                        <ArrowDownIcon className='w-5 h-5' />
                      </div>
                    }>
                    <DropdownList className='!p-0 !max-h-fit'>
                      <Calendar
                        date={moment(item.expiryDate).toDate()}
                        onChange={(item: Date) => {
                          setValue(index, 'expiryDate', item.toDateString());
                        }}
                        showDateDisplay={() => setShowDateModal({ idx: index, show: true })}
                        minDate={new Date()}
                        className='calendar'
                      />
                    </DropdownList>
                  </Dropdown>
                </div>

                <div className='space-y-2 col-span-2'>
                  <label>Upload invoice(s)</label>
                  <FileUploader
                    fileUrls={item?.invoiceUrls ? item?.invoiceUrls : []}
                    setFileUrls={(imageUrls: any) => {
                      setValue(index, 'quantity_returned', imageUrls);
                    }}
                    token={FILE_UPLOADER_TOKEN}
                    className='w-full border-none p-0 m-0'
                  />
                </div>
              </div>
            </div>
          </div>
        )
      )}
      <div className='flex justify-end gap-4 mt-6 items-center mb-[40px] pr-[28px]'>
        <p className='text-sm text-[#6F7376]'>Total</p>
        <h4 className='text-[#2E3031] text-2xl font-semibold'>
          ƒ,İ {Number(procurementToUpdate.expected_cost).toLocaleString()}
        </h4>
      </div>
      <div>
        <hr className='border-t-4 border-[#F7F7F8]' />
        <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
          <Button variant='link' color='gray' small onClick={() => router(-1)}>
            Cancel
          </Button>
          <Button
            variant='block'
            color='primary'
            small
            className='w-[153px] h-[43px]'
            disabled={!isFormFilled()}
            onClick={updateProcurementDetails}
            loading={isUpdatingProcurement}>
            Confirm
          </Button>
        </div>
      </div>
    </RequestDetailsWrapper>
  );
};

export default UpdateKitchenRequestDetails;
